import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { supabase } from '../services/supabaseClient';

type Mode = 'emprendedor' | 'contador';

interface RoleContextValue {
  mode: Mode;
  setMode: (m: Mode) => void;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
}

const RoleContext = createContext<RoleContextValue>({
  mode: 'contador',
  setMode: () => {},
  isCollapsed: false,
  toggleCollapsed: () => {},
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(() =>
    (localStorage.getItem('quipus_mode') as Mode) || 'contador',
  );
  const [isCollapsed, setIsCollapsed] = useState(() =>
    localStorage.getItem('quipus_sidebar_collapsed') === 'true',
  );

  // Sync mode from Supabase user_metadata on mount (cross-device persistence)
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const cloudMode = session?.user?.user_metadata?.quipus_mode as Mode | undefined;
      if (cloudMode === 'emprendedor' || cloudMode === 'contador') {
        setModeState(cloudMode);
        localStorage.setItem('quipus_mode', cloudMode);
      }
    });
  }, []);

  const setMode = useCallback((m: Mode) => {
    setModeState(m);
    localStorage.setItem('quipus_mode', m);
    // Persist to Supabase user metadata (fire-and-forget)
    supabase.auth.updateUser({ data: { quipus_mode: m } }).catch(() => {});
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('quipus_sidebar_collapsed', String(next));
      return next;
    });
  }, []);

  return (
    <RoleContext.Provider value={{ mode, setMode, isCollapsed, toggleCollapsed }}>
      {children}
    </RoleContext.Provider>
  );
}

export const useRoleContext = () => useContext(RoleContext);
